

import { CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, FileText } from "lucide-react"
import { useState } from "react"
import type { Resumo, Evento } from "@/types/processador-previdenciario"
import { cn } from "@/lib/utils"

function getEventStatus(evento: Evento) {
  const code = evento.processamento.cdResposta
  if (code === "201") return "success"
  if (code === "202") return "warning"
  return "error"
}

function EventRow({ evento, index }: { evento: Evento; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const status = getEventStatus(evento)
  const hasOcorrencias = evento.processamento.ocorrencias.length > 0

  return (
    <div
      className={cn(
        "border-b border-border/50 last:border-b-0",
        status === "error" && "bg-destructive/5",
        status === "warning" && "bg-warning/5"
      )}
    >
      <button
        onClick={() => hasOcorrencias && setExpanded(!expanded)}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors",
          hasOcorrencias && "cursor-pointer hover:bg-accent/50"
        )}
        disabled={!hasOcorrencias}
        type="button"
      >
        <span className="text-xs font-mono text-muted-foreground w-6 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>

        {status === "success" && <CheckCircle2 className="h-4 w-4 text-success shrink-0" />}
        {status === "warning" && <AlertTriangle className="h-4 w-4 text-warning shrink-0" />}
        {status === "error" && <XCircle className="h-4 w-4 text-destructive shrink-0" />}

        <span className="flex-1 text-sm font-mono text-muted-foreground truncate">
          {evento.id}
        </span>

        <span
          className={cn(
            "text-xs font-medium px-2 py-0.5 rounded-full shrink-0",
            status === "success" && "bg-success/10 text-success",
            status === "warning" && "bg-warning/10 text-warning",
            status === "error" && "bg-destructive/10 text-destructive"
          )}
        >
          {evento.processamento.cdResposta} - {evento.processamento.descResposta}
        </span>

        {hasOcorrencias && (
          <span className="shrink-0 text-muted-foreground">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        )}
      </button>

      {expanded && hasOcorrencias && (
        <div className="px-4 pb-3 pl-14">
          {evento.processamento.ocorrencias.map((oc, oi) => (
            <div
              key={oi}
              className={cn(
                "rounded-lg p-3 text-sm mt-2",
                oc.tipo === "1" ? "bg-destructive/10 border border-destructive/20" : "bg-warning/10 border border-warning/20"
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={cn(
                    "text-xs font-bold px-1.5 py-0.5 rounded",
                    oc.tipo === "1" ? "bg-destructive/20 text-destructive" : "bg-warning/20 text-warning"
                  )}
                >
                  {oc.tipo === "1" ? "ERRO" : "AVISO"} {oc.codigo}
                </span>
              </div>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed text-xs">
                {oc.descricao}
              </p>
              {oc.localizacao && (
                <p className="mt-2 text-xs font-mono text-muted-foreground/70">
                  {oc.localizacao}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

interface StepResultProps {
  data: Resumo
  stepName: string
}

export function StepResult({ data, stepName }: StepResultProps) {
  const [showEvents, setShowEvents] = useState(false)

  const successCount = data.eventos.filter((e) => e.processamento.cdResposta === "201").length
  const warningCount = data.eventos.filter((e) => e.processamento.cdResposta === "202").length
  const errorCount = data.eventos.filter((e) => !["201", "202"].includes(e.processamento.cdResposta)).length
  const total = data.eventos.length

  return (
    <div className="mt-3 rounded-xl border border-border bg-card overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
      {/* Summary Header */}
      <div className="px-4 py-3 border-b border-border/50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">{stepName}</span>
          </div>
          <span className="text-xs text-muted-foreground font-mono">
            {data.lote.protocoloEnvio}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="h-2 w-2 rounded-full bg-success" />
            <span className="text-xs text-muted-foreground">
              {successCount} sucesso{successCount !== 1 ? "s" : ""}
            </span>
          </div>
          {warningCount > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-warning" />
              <span className="text-xs text-muted-foreground">
                {warningCount} aviso{warningCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
          {errorCount > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="h-2 w-2 rounded-full bg-destructive" />
              <span className="text-xs text-muted-foreground">
                {errorCount} erro{errorCount !== 1 ? "s" : ""}
              </span>
            </div>
          )}
          <span className="text-xs text-muted-foreground ml-auto">{total} evento{total !== 1 ? "s" : ""}</span>
        </div>

        {/* Progress Bar */}
        <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden flex">
          {successCount > 0 && (
            <div className="h-full bg-success" style={{ width: `${(successCount / total) * 100}%` }} />
          )}
          {warningCount > 0 && (
            <div className="h-full bg-warning" style={{ width: `${(warningCount / total) * 100}%` }} />
          )}
          {errorCount > 0 && (
            <div className="h-full bg-destructive" style={{ width: `${(errorCount / total) * 100}%` }} />
          )}
        </div>
      </div>

      {/* Toggle Events List */}
      <button
        onClick={() => setShowEvents(!showEvents)}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        type="button"
      >
        {showEvents ? "Ocultar detalhes" : "Ver detalhes dos eventos"}
        {showEvents ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {/* Events List */}
      {showEvents && (
        <div className="max-h-80 overflow-y-auto border-t border-border/50">
          {data.eventos.map((evento, i) => (
            <EventRow key={evento.id} evento={evento} index={i} />
          ))}
        </div>
      )}
    </div>
  )
}
